import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface BackgroundTaskResult {
  imported?: number;
  updated?: number;
  skipped?: number;
  errors?: number;
  // Image import specifics
  uploadedImages?: number;
  failedFiles?: number;
}

export interface BackgroundTask {
  id: string;
  type: 'csv-import' | 'image-import';
  status: 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled';
  label: string;
  progress: number;
  totalItems: number;
  processedItems: number;
  result?: BackgroundTaskResult;
  createdAt: number;
}

interface BackgroundTaskState {
  tasks: BackgroundTask[];
  addTask: (task: BackgroundTask) => void;
  updateTask: (id: string, partial: Partial<BackgroundTask>) => void;
  removeTask: (id: string) => void;
  clearCompleted: () => void;
}

export const useBackgroundTaskStore = create<BackgroundTaskState>()(
  persist(
    (set) => ({
      tasks: [],

      addTask: (task) =>
        set((state) => {
          // Replace existing task with same id, or add new
          const exists = state.tasks.find((t) => t.id === task.id);
          if (exists) {
            return {
              tasks: state.tasks.map((t) => (t.id === task.id ? task : t)),
            };
          }
          return { tasks: [...state.tasks, task] };
        }),

      updateTask: (id, partial) =>
        set((state) => ({
          tasks: state.tasks.map((t) =>
            t.id === id ? { ...t, ...partial } : t
          ),
        })),

      removeTask: (id) =>
        set((state) => ({
          tasks: state.tasks.filter((t) => t.id !== id),
        })),

      clearCompleted: () =>
        set((state) => ({
          tasks: state.tasks.filter(
            (t) => !['completed', 'failed', 'cancelled'].includes(t.status)
          ),
        })),
    }),
    {
      name: 'background_tasks',
      // Only persist active/recent tasks, clean up stale ones on rehydrate
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Remove tasks that completed more than 5 minutes ago
          const fiveMinAgo = Date.now() - 5 * 60 * 1000;
          state.tasks = state.tasks.filter(
            (t) =>
              !['completed', 'failed', 'cancelled'].includes(t.status) ||
              t.createdAt > fiveMinAgo
          );
        }
      },
    }
  )
);

// Helper selectors
export const getActiveTasks = (state: BackgroundTaskState) =>
  state.tasks.filter(
    (t) => !['completed', 'failed', 'cancelled'].includes(t.status)
  );

export const getActiveTaskByType = (
  state: BackgroundTaskState,
  type: BackgroundTask['type']
) =>
  state.tasks.find(
    (t) => t.type === type && !['completed', 'failed', 'cancelled'].includes(t.status)
  );
